import { Box, Typography } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import {
  useCreateCustomVariableValue,
  useDeleteCustomVariableValue,
  useUpdateCustomVariableValue,
} from '@/service-library/hooks/custom-variable-values'
import {
  CustomVariable,
  CustomVariableValue as TSCustomVariableValue,
} from '@/types/custom-variables'
import { Organization } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import PixyMarkdown from '@/components/markdown/PixyMarkdown'
import CustomVariableValue from './CustomVariableValue'

type CustomVariableValueRowProps = {
  variable: CustomVariable
  customVariableValuesQueryKey: QueryKey
  resolvedCustomVariableValuesQueryKey: QueryKey
  value?: TSCustomVariableValue
  resolvedValue?: string | null
  organization: Organization
}

export default function CustomVariableValueRow({
  variable,
  customVariableValuesQueryKey,
  resolvedCustomVariableValuesQueryKey,
  value,
  resolvedValue,
  organization,
}: CustomVariableValueRowProps) {
  const { isAuthorized: canEditOrgs } = useStytchIsAuthorized('pd.org', 'edit')

  const { createCustomVariableValue } = useCreateCustomVariableValue({
    sideEffectQueryKeys: [resolvedCustomVariableValuesQueryKey],
    listQueryKey: customVariableValuesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to create value. Please try again later.')
    },
  })
  const { deleteCustomVariableValue } = useDeleteCustomVariableValue({
    sideEffectQueryKeys: [resolvedCustomVariableValuesQueryKey],
    listQueryKey: customVariableValuesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to delete value. Please try again later.')
    },
  })
  const { updateCustomVariableValue } = useUpdateCustomVariableValue({
    sideEffectQueryKeys: [resolvedCustomVariableValuesQueryKey],
    listQueryKey: customVariableValuesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to update value. Please try again later.')
    },
  })

  function handleCreateUpdateValue({
    value,
    customVariableId,
    customVariableValue,
  }: {
    value: string
    customVariableId: string
    customVariableValue?: TSCustomVariableValue
  }) {
    if (!organization) return
    if (!customVariableValue) {
      createCustomVariableValue({
        id: generateUuid(),
        value,
        custom_variable_id: customVariableId,
        owner_org_id: organization.id,
        project_id: null,
      })
    } else {
      updateCustomVariableValue({ ...customVariableValue, value })
    }
  }

  return (
    <>
      <Box>
        <Typography
          sx={{ overflowWrap: 'break-word', py: '2px' }}
          variant="body2"
        >
          {variable.name}
        </Typography>
      </Box>

      <CustomVariableValue
        canEdit={canEditOrgs}
        isRootOrg={!organization.parent_org_id}
        variableValue={value}
        resolvedValue={resolvedValue || null}
        handleOnBlur={(newValue) => {
          handleCreateUpdateValue({
            value: newValue,
            customVariableId: variable.id,
            customVariableValue: value,
          })
        }}
        onDelete={deleteCustomVariableValue}
        organization={organization}
      />

      <Box>
        <PixyMarkdown
          components={{
            p: ({ children }) => (
              <Typography variant="body2" color="text.secondary">
                {children}
              </Typography>
            ),
          }}
        >
          {variable.description || ''}
        </PixyMarkdown>
      </Box>
    </>
  )
}
